import { IAlternative, IHotel, ITotalCost } from "../../../hotel/models/hotel";

import { ICurrency } from "../model/search/ICurrency";
import { IAccommodation, IMealplan } from "libraries/hotel/models/details";
import { ICost } from "../model/search/IBooking";
import { IRating } from "./ISearchParameters";
import { IRooms } from "./IResourceHotel";
import { IBookingPropertyDescription } from "../booking/interfaces/IGetBookings";

//////////////////////////////////// SEARCH REPONSE INTERFACES ////////////////////////////////////

export interface IRootQuoteData {
    data: ISearchOffersResponse;
}

export enum HotelQuoteStatus {
    Available = "Available",
    Request = "Request"
}

export interface IHotelQuoteResponse {
    key: string;
    status: HotelQuoteStatus;
    message: string[];
    topic: string;
    traveller: ITravellers;
    segment: ISegment[];
    quantity: IQuantity;
    quote: IQuotes;
    rooming: IRooming;
    currency?: ICurrencies;
    error?: {
        active: boolean;
        message: string;
    };
}


export interface IHotelRoomResponse {
    currency: ICurrencies;
    key: string;
    property: IProperties;
    quantity: IQuantity;
    room: {
        [key: string]: IPossibleRoom;
    };
    rooming: IRooming;
    segment: ISegment[];
    status: string;
    topic: "room";
    traveller: ITravellers;
}

export interface IPossibleRoom {
    code: string;
    dateLastCancellationWithoutPenalty: string;
    mealplan: IMealplan;
    name: {
        text: string;
        language: string;
    };
    rooming: string[];
    syscode: string;
    unit: {
        cost: ICost
        retail?: ICost
    };
    xCode: string;
    xId: string;
    xType: string;
}

export interface ISearchOffersResponse extends IHotelQuoteResponse {
    property: IProperties;
    room: IRooms;
    transactionId?: string;
    isOnlyRefundable?: boolean;
}

export interface IUiResponse extends IHotelQuoteResponse {
    property: IHotel[];
}

export interface ICurrencies {
    [key: string]: ICurrency;
}

export interface IRooming {
    [key: string]: ITravellers;
}

export interface IQuotes {
    [key: string]: ICompleteQuote;
}

export interface IQuote {
    hostRoomNameArray?: string[];
    property: string;
    host: string;
    xId?: string;
    total: ITotal;
    rank: string;
    transactionId?: string;
    dateLastCancellationWithoutPenalty: string;
    alternative: string[];
    mealplan?: IMealplan;
    status: HotelQuoteStatus;
}

export interface ICompleteQuote extends IQuote {
    isNotSelectable?: boolean;
    detail: {
        [key: string]: IAccommodation | IMealplan
    };
}

export interface ITotal {
    cost: ITotalCost;
}

export interface IProperties {
    [key: string]: IHotel;
}

export interface IHosts {
    [key: string]: {
        syscode: string;
        owner: string;
        code: string;
    };
}

export interface IGeoposition {
    latitude: string;
    longitude: string;
}

export interface IQuantity {
    adult: number;
    child: number;
    infant: number;
    rooming: number;
    property: number;
    quote: number;
}

export interface ITravellers {
    [key: string]: ITraveller;
}

export interface ITraveller {
    id: string;
    type: 'adult' | 'child' | 'infant';
    typeInput: string;
    xId: string;
}

export interface ISegment {
    begin: string;
    end: string;
    duration: IDuration;
    property: ISegmentProperty;
    rating: IRating;
}

export interface IDuration {
    night: number;
}

export interface ISegmentProperty {
    host: string[];
    text: string;
}

export interface ISegmentRooming {
    [key: string]: ISegmentRoomingTraveller;
}

export interface ISegmentRoomingTraveller {
    traveller: string[];
}

export interface ITripPropertyQuote {
    trip: string;
    property: IHotel;
    quote: IQuote;
}

//////////////////////////////////// GET PROPERTY RESPONSES INTERFACES ////////////////////////////////////
export interface IProperty extends IHotel {
    date: IPropertyDate;
    room: IPropertyRoom;
    brand: string;
    key?: string;
    message?: string[];
    floor: number;
    media: { [key: string]: IPropertyMedia | IPropertyMedia[] };
    state: string;
    languageCode?: string;
    nature: string;
    airport: string[];
    content: string;
    customer: string;
    distance: { [key: string]: IDistance };
    language: string;
    template: string;
    description: IPropertyDescription;
    introduction: IPropertyIntroduction;
    formOfPayment: { [key: string]: ICodeName };
    credit?: IPropertyCredit;
    alternatives?: IAlternative[];
    descriptions?: IBookingPropertyDescription[];
    hostCode?: string;
    hostShortcut?: string;
    sourceCode?: string;
    sourceShortcut?: string;
    mealplans?: IMealplan[];
    medias?: IPropertyMedia[];
    targetLanguageCode?: string;
    code?: string;
    keywords?: string[];
    keywordXCodes?: string;


}

export interface IPropertyDescription {
    [key: string]: {
        [key: string]: ITextTitleLanguage
    };
}

export interface IPropertyCredit {
    host: string;
    text: string;
}

export interface IGetPropertyResponse {
    property: IProperty;
    description?: {
        languageCode: string;
    };
}

export interface IDistance extends ICodeName {
    value: number;
}

interface ICodeName {
    code: string;
    name: {
        text: string;
        language: string;
    };
}

interface ITextTitleLanguage {
    text: string;
    title: string;
    language: string;
}

export interface IPropertyIntroduction extends ITextTitleLanguage {
    media: {
        link: string;
        size: {
            unit: string;
            width: string;
            height: string;
        };
        weight: {
            unit: string;
            value: string;
        }
    };
}

export interface IPropertyMedia {
    zoom: IPropertyMediaDetail;
    image: IPropertyMediaDetail;
    vignette: IPropertyMediaDetail;
}

export interface IPropertyMediaDetail {
    url: string;
    width: string;
    height: string;
    weight: string;
}

export interface IPropertyRoom {
    total: number;
    keyword: {
        [key: string]: ICodeName
    };
}

export interface IPropertyDate {
    renovation: string;
    construction: string;
}
